import React, {
    useState,
    useEffect,
    Fragment,
    ChangeEvent,
    MouseEvent,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import SimpleDialog from 'src/components/SimpleDialog';
import useAuth from 'src/hooks/useAuth';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import SkeletonTable from 'src/components/SkeletonTable';
import CustomPagination from 'src/components/CustomPagination';
import {
    Box,
    Card,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Checkbox,
    makeStyles,
    Divider,
} from '@material-ui/core';
import Label from 'src/components/Label';
import { AdvancedFilters } from 'src/components/MaintenancePackages/Filters';
import {
    ADMIN_LEVEL_ACCESS,
    DEVELOPER_LEVEL_ACCESS,
    GROUP_LEVEL_ACCESS,
} from 'src/components/access';
import {
    LOCAL_STORAGE_MAINTENANCE_PACKAGE_ROWS,
    MAINTENANCE_PACKAGE_VISIBLE_FIELDS,
} from './constants';
import { VisibleRows } from 'src/components/Tables/VisibleRows';
import { useMaintenancePackage } from 'src/hooks/useMaintenancePackage';
import { getDefaultMaintenancePackagesFilters } from 'src/components/MaintenancePackages/Filters/utils';
import { Plus } from 'react-feather';
import {
    FiltersSummary,
    FiltersTabs,
    BulkActionsHeader,
} from 'src/components/Search';
import { FiltersHeader } from 'src/components/Search/components/FiltersHeader';
import {
    canCreateMaintenancePackage,
    canDeleteMaintenancePackage,
} from 'src/modules/maintenancePackages/helpers/permissionsInMaintenancePackages';
import {
    MAINTENANCE_PACKAGES_TABS_FILTERS,
    NO_DISPLAYABLE_MAINTENANCE_PACKAGE_FILTERS,
} from 'src/components/MaintenancePackages/Filters/constants';
import { IMaintenancePackage } from 'src/interfaces/maintenancePackage';
import { DEFAULT_SERVICE_DETAILS } from 'src/constants/maintenancePackage';
import { bulkDeleteMaintenancePackages } from 'src/contexts/maintenancePackage/utils';

const useStyles = makeStyles((theme) => ({
    tableHead: {
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 2,
    },
    cell: {
        paddingTop: 3,
        paddingBottom: 3,
    },
}));

export const Results = ({
    values,
    setValues,
    loadInformation,
    query,
    setQuery,
    ...props
}: any) => {
    const { user, loading: loadingUser } = useAuth();
    const { t } = useTranslation();
    const classes = useStyles();
    const [selectedMaintenancePackages, setSelectedMaintenancePackages] =
        useState<string[]>([]);
    const {
        total,
        maintenancePackages,
        bulkDeleteMaintenancePackages,
        loading: loadingMaintenancePackages,
    } = useMaintenancePackage();
    const [multipleDelete, setMultipleDelete] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<
        'yes' | 'no' | undefined
    >();
    const [isAdvancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [modalRowOpen, setModalRowOpen] = useState(false);
    const [visibleRows, setVisibleRows] = useState<Record<string, boolean>>({
        id: true,
        name: true,
        'details.title': true,
        isActive: true,
        createdAt: true,
    });

    useEffect(() => {
        if (localStorage.getItem(LOCAL_STORAGE_MAINTENANCE_PACKAGE_ROWS)) {
            const rows = localStorage.getItem(
                LOCAL_STORAGE_MAINTENANCE_PACKAGE_ROWS
            );
            if (!rows) return;
            setVisibleRows(JSON.parse(rows));
        } else {
            localStorage.setItem(
                LOCAL_STORAGE_MAINTENANCE_PACKAGE_ROWS,
                JSON.stringify(visibleRows)
            );
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(
            LOCAL_STORAGE_MAINTENANCE_PACKAGE_ROWS,
            JSON.stringify(visibleRows)
        );
    }, [visibleRows]);

    useEffect(() => {
        if (multipleDelete) setMultipleDelete(false);
    }, [multipleDelete]);

    const handleClose = async (value: 'yes' | 'no') => {
        setOpen(false);
        setSelectedValue(value);
        if (value === 'yes') {
            bulkDeleteMaintenancePackages(
                {
                    filters: { _id: { $in: selectedMaintenancePackages } },
                },
                loadInformation
            );
            setSelectedMaintenancePackages([]);
        }
    };

    const handleSelectAllMaintenancePackages = async (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        setSelectedMaintenancePackages(
            event.target.checked
                ? (maintenancePackages || []).map(
                      (maintenancePackage) => maintenancePackage._id
                  )
                : []
        );
    };

    const handleSelectOneMaintenancePackage = (
        _: ChangeEvent<HTMLInputElement>,
        maintenancePackageId: string
    ) => {
        if (!selectedMaintenancePackages.includes(maintenancePackageId)) {
            setSelectedMaintenancePackages((prevSelected) => [
                ...prevSelected,
                maintenancePackageId,
            ]);
        } else {
            setSelectedMaintenancePackages((prevSelected) =>
                prevSelected.filter((id) => id !== maintenancePackageId)
            );
        }
    };

    const enableBulkOperations = selectedMaintenancePackages.length > 0;
    const selectedSomeMaintenancePackages =
        selectedMaintenancePackages.length > 0 &&
        selectedMaintenancePackages.length < (maintenancePackages?.length || 0);
    const selectedAllMaintenancePackages =
        selectedMaintenancePackages.length === maintenancePackages?.length;

    const PaginationActions = (props: any) => {
        return (
            <CustomPagination
                page={props.page}
                handlePageChange={props.onPageChange}
                limit={props.rowsPerPage}
                count={props.count}
            />
        );
    };

    const handlePageChange = (
        _: MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => setValues({ ...values, page: newPage });

    const handleLimitChange = (event: ChangeEvent<HTMLInputElement>) =>
        setValues({ ...values, limit: parseInt(event.target.value) });

    return (
        <Fragment>
            <AdvancedFilters
                open={isAdvancedSearchOpen}
                setOpen={setAdvancedSearchOpen}
                onApply={(filters) => setValues(filters)}
                values={values}
            />
            <VisibleRows
                fields={MAINTENANCE_PACKAGE_VISIBLE_FIELDS}
                visibleRows={visibleRows}
                setVisibleRows={setVisibleRows}
                open={modalRowOpen}
                setOpen={setModalRowOpen}
            />

            <SimpleDialog
                selectedValue={selectedValue}
                open={open}
                onClose={handleClose}
            />

            <Card {...props} elevation={0}>
                <FiltersTabs
                    values={values}
                    setValues={setValues}
                    disable={loadingMaintenancePackages}
                    tabs={MAINTENANCE_PACKAGES_TABS_FILTERS}
                    defaultFilters={getDefaultMaintenancePackagesFilters()}
                />
                <Divider />
                {enableBulkOperations && canDeleteMaintenancePackage(user) ? (
                    <BulkActionsHeader setDeleteModalOpen={setOpen} />
                ) : (
                    <FiltersHeader
                        advancedSearch={{
                            setAdvancedSearchOpen,
                            isEnabled: !values.isTabsEnabled,
                        }}
                        queryField={{
                            searchLabel: 'maintenancePackage.Search',
                            isEnabled: !(
                                loadingMaintenancePackages || loadingUser
                            ),
                        }}
                        create={
                            canCreateMaintenancePackage(user)
                                ? {
                                      createLabel: 'maintenancePackage.Create',
                                      createPath: '/create/maintenance-package',
                                      icon: <Plus />,
                                  }
                                : {
                                      createLabel: '',
                                      createPath: '',
                                      icon: null,
                                  }
                        }
                        setModalRowOpen={setModalRowOpen}
                        loadInformation={{
                            loadInformation,
                            isEnabled: !(
                                loadingMaintenancePackages || loadingUser
                            ),
                        }}
                        setValues={setValues}
                        values={values}
                    />
                )}

                <Divider />

                {values.isSearching && (
                    <FiltersSummary
                        filters={values}
                        onClear={() =>
                            setValues(getDefaultMaintenancePackagesFilters())
                        }
                        noDisplayableFilters={
                            NO_DISPLAYABLE_MAINTENANCE_PACKAGE_FILTERS
                        }
                    />
                )}
                <PerfectScrollbar>
                    <Box minWidth={700} style={{ maxHeight: '70vh' }}>
                        <Table>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    {user?._id &&
                                        canDeleteMaintenancePackage(user) && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={
                                                        selectedAllMaintenancePackages
                                                    }
                                                    indeterminate={
                                                        selectedSomeMaintenancePackages
                                                    }
                                                    onChange={
                                                        handleSelectAllMaintenancePackages
                                                    }
                                                />
                                            </TableCell>
                                        )}
                                    {visibleRows.id && (
                                        <TableCell className={classes.cell}>
                                            ID
                                        </TableCell>
                                    )}

                                    {visibleRows.name && (
                                        <TableCell
                                            style={{ minWidth: '15ch' }}
                                            className={classes.cell}
                                        >
                                            {t('maintenancePackage.Name')}
                                        </TableCell>
                                    )}

                                    {visibleRows.details && (
                                        <TableCell className={classes.cell}>
                                            {t('maintenancePackage.Details')}
                                        </TableCell>
                                    )}

                                    {visibleRows.createdAt && (
                                        <TableCell
                                            style={{ minWidth: '20ch' }}
                                            className={classes.cell}
                                        >
                                            {t('maintenancePackage.CreatedAt')}
                                        </TableCell>
                                    )}

                                    {visibleRows.isActive && (
                                        <TableCell
                                            style={{ minWidth: '15ch' }}
                                            className={classes.cell}
                                        >
                                            {t('maintenancePackage.IsActive')}
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loadingMaintenancePackages ? (
                                    maintenancePackages?.map(
                                        (
                                            maintenancePackage: IMaintenancePackage
                                        ) => {
                                            const isMaintenancePackageSelected =
                                                selectedMaintenancePackages.includes(
                                                    maintenancePackage._id
                                                );

                                            return (
                                                <TableRow
                                                    hover
                                                    key={maintenancePackage._id}
                                                    selected={
                                                        isMaintenancePackageSelected
                                                    }
                                                >
                                                    {user?._id &&
                                                        canDeleteMaintenancePackage(
                                                            user
                                                        ) && (
                                                            <TableCell
                                                                padding="checkbox"
                                                                className={
                                                                    classes.cell
                                                                }
                                                            >
                                                                <Checkbox
                                                                    checked={
                                                                        isMaintenancePackageSelected
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        handleSelectOneMaintenancePackage(
                                                                            event,
                                                                            maintenancePackage._id
                                                                        )
                                                                    }
                                                                    value={
                                                                        isMaintenancePackageSelected
                                                                    }
                                                                />
                                                            </TableCell>
                                                        )}

                                                    {visibleRows.id && (
                                                        <TableCell>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                            >
                                                                <div>
                                                                    <Link
                                                                        color="inherit"
                                                                        component={
                                                                            RouterLink
                                                                        }
                                                                        to={`/management/maintenance-packages/${maintenancePackage._id}`}
                                                                        variant="h6"
                                                                    >
                                                                        {
                                                                            maintenancePackage._id
                                                                        }
                                                                    </Link>
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                    )}

                                                    {visibleRows.name && (
                                                        <TableCell>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                            >
                                                                <div>
                                                                    <Link
                                                                        color="inherit"
                                                                        component={
                                                                            RouterLink
                                                                        }
                                                                        to={`/management/maintenance-packages/${maintenancePackage._id}`}
                                                                        variant="h6"
                                                                    >
                                                                        {
                                                                            maintenancePackage.name
                                                                        }
                                                                    </Link>
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                    )}

                                                    {visibleRows.details && (
                                                        <TableCell>
                                                            {maintenancePackage.details.map(
                                                                (
                                                                    detail,
                                                                    index
                                                                ) => (
                                                                    <ul
                                                                        key={
                                                                            index
                                                                        }
                                                                        style={{
                                                                            display:
                                                                                '-webkit-box',
                                                                            WebkitLineClamp: 2,
                                                                            WebkitBoxOrient:
                                                                                'vertical',
                                                                            overflow:
                                                                                'hidden',
                                                                            textOverflow:
                                                                                'ellipsis',
                                                                            marginBottom: 10,
                                                                        }}
                                                                    >
                                                                        <li>
                                                                            <span
                                                                                style={{
                                                                                    fontWeight: 500,
                                                                                }}
                                                                            >
                                                                                {DEFAULT_SERVICE_DETAILS.includes(
                                                                                    detail.title
                                                                                )
                                                                                    ? t(
                                                                                          detail.title
                                                                                      )
                                                                                    : detail.title}
                                                                            </span>
                                                                            {
                                                                                ': '
                                                                            }

                                                                            {
                                                                                detail.description
                                                                            }
                                                                        </li>
                                                                    </ul>
                                                                )
                                                            )}
                                                        </TableCell>
                                                    )}

                                                    {visibleRows.createdAt && (
                                                        <TableCell>
                                                            <p>
                                                                {moment(
                                                                    maintenancePackage.createdAt
                                                                ).format(
                                                                    'DD MMMM YYYY'
                                                                )}{' '}
                                                                <br />{' '}
                                                                {moment(
                                                                    maintenancePackage.createdAt
                                                                ).format(
                                                                    'hh:mm a'
                                                                )}
                                                            </p>
                                                        </TableCell>
                                                    )}

                                                    {visibleRows.isActive && (
                                                        <TableCell>
                                                            <Label
                                                                color={
                                                                    maintenancePackage.isActive
                                                                        ? 'success'
                                                                        : 'error'
                                                                }
                                                            >
                                                                {t(
                                                                    `maintenancePackage.${
                                                                        maintenancePackage.isActive
                                                                            ? 'Active'
                                                                            : 'Inactive'
                                                                    }`
                                                                )}
                                                            </Label>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        }
                                    )
                                ) : (
                                    <SkeletonTable
                                        size={values.limit}
                                        rows={[
                                            'id',
                                            'name',
                                            'details',
                                            'isActive',
                                            'createdAt',
                                        ]}
                                        check={
                                            user.accessLevel ===
                                                DEVELOPER_LEVEL_ACCESS ||
                                            user.accessLevel ===
                                                ADMIN_LEVEL_ACCESS ||
                                            user.accessLevel ===
                                                GROUP_LEVEL_ACCESS
                                        }
                                        visibleRows={visibleRows}
                                        width={150}
                                        height={30}
                                    />
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </PerfectScrollbar>
                <TablePagination
                    component="div"
                    count={total || 0}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleLimitChange}
                    page={values.page}
                    rowsPerPage={values.limit}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ActionsComponent={PaginationActions}
                />
            </Card>
        </Fragment>
    );
};
